import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { services, team, locations, news, clinicEmail, clinicPhone } from '../mock';

const siteUrl = 'https://uptownphysiotherapyclinic.ca';
const clinicName = 'Uptown Physiotherapy Clinic';
const logoUrl = `${siteUrl}/uptown-logo.svg`;
const defaultImage = `${siteUrl}/team/eric-sauerwein.png`;
const defaultDescription =
  'Uptown Physiotherapy Clinic in Windsor provides physiotherapy, rehabilitation, sports injury care, occupational therapy, and more.';
const defaultKeywords =
  'physiotherapy Windsor, physiotherapist Windsor Ontario, Uptown Physiotherapy Clinic, sports injury rehab, concussion rehab, vestibular rehab';

const clinic = {
  '@type': ['Physiotherapy', 'MedicalClinic', 'LocalBusiness'],
  '@id': `${siteUrl}/#clinic`,
  name: clinicName,
  url: siteUrl,
  logo: logoUrl,
  image: logoUrl,
  telephone: clinicPhone,
  email: clinicEmail,
  priceRange: '$$',
  medicalSpecialty: 'Physiotherapy',
  isAcceptingNewPatients: true,
  hasMap: 'https://www.google.com/maps?q=960+Tecumseh+Rd+E,+Windsor,+ON+N8X+2S6',
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 42.3038,
    longitude: -83.0147,
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '960 Tecumseh Rd E',
    addressLocality: 'Windsor',
    addressRegion: 'ON',
    postalCode: 'N8X 2S6',
    addressCountry: 'CA',
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00',
    },
  ],
  areaServed: [
    'Windsor',
    'Walkerville',
    'South Walkerville',
    'Remington Park',
    'Riverside',
    'Forest Glade',
    'Tecumseh',
    'LaSalle',
    'Lakeshore',
    'Amherstburg',
    'Essex County',
  ],
  availableService: services.map((service) => ({
    '@type': 'MedicalTherapy',
    name: service.name,
    url: `${siteUrl}/services/${service.slug}`,
  })),
};

const absolute = (path) => {
  if (!path) return siteUrl;
  if (path.startsWith('http')) return path;
  return `${siteUrl}${path.startsWith('/') ? path : `/${path}`}`;
};

const pageTitle = (pathname) => {
  if (pathname === '/') return `${clinicName} | Physiotherapy & Rehab in Windsor`;
  if (pathname === '/about') return `About ${clinicName}`;
  if (pathname === '/services') return `Physiotherapy Services | ${clinicName}`;
  if (pathname === '/team') return `Our Team | ${clinicName}`;
  if (pathname === '/locations') return `Windsor Physiotherapy Location | ${clinicName}`;
  if (pathname === '/news') return `Physiotherapy Blog | ${clinicName}`;
  if (pathname === '/contact') return `Contact ${clinicName}`;
  if (pathname === '/appointment') return `Call to Book an Appointment | ${clinicName}`;
  if (pathname === '/privacy') return `Privacy & Ontario Care Standards | ${clinicName}`;
  if (pathname === '/terms') return `Terms & Conditions | ${clinicName}`;
  if (pathname === '/privacy-policy') return `Privacy Policy | ${clinicName}`;
  if (pathname === '/legal-notice') return `Legal Notice | ${clinicName}`;

  const [, section, slug] = pathname.split('/');
  if (section === 'services') {
    const service = services.find((item) => item.slug === slug);
    return service ? `${service.name} in Windsor | ${clinicName}` : clinicName;
  }
  if (section === 'team') {
    const member = team.find((item) => item.slug === slug);
    return member ? `${member.name} | ${clinicName}` : clinicName;
  }
  if (section === 'locations') {
    const location = locations.find((item) => item.slug === slug);
    return location ? `${location.name} | ${clinicName}` : clinicName;
  }
  if (section === 'news') {
    const article = news.find((item) => item.slug === slug);
    return article ? `${article.title} | ${clinicName}` : clinicName;
  }
  return clinicName;
};

const descriptionFor = (pathname) => {
  const [, section, slug] = pathname.split('/');
  if (pathname === '/') return 'Physiotherapy, sports injury rehab, concussion care, and chronic pain treatment from Uptown Physiotherapy Clinic in Windsor, Ontario.';
  if (pathname === '/about') return 'Learn about Uptown Physiotherapy Clinic, a Windsor physiotherapy clinic focused on evidence-informed rehab, respectful care, and practical recovery plans.';
  if (pathname === '/services') return 'Explore physiotherapy, athletic therapy, vestibular rehab, concussion rehab, occupational therapy, and more in Windsor.';
  if (pathname === '/team') return 'Meet the Uptown Physiotherapy Clinic team of registered physiotherapists, athletic therapists, and rehabilitation support staff.';
  if (pathname === '/locations') return 'Visit Uptown Physiotherapy Clinic in Windsor for physiotherapy, rehabilitation, sports injury care, and patient-focused treatment.';
  if (pathname === '/news') return 'Read practical physiotherapy articles about pain, injury prevention, rehabilitation, sports injuries, and recovery tips.';
  if (pathname === '/contact') return 'Contact Uptown Physiotherapy Clinic in Windsor to ask a question, find the clinic, or speak with the reception team.';
  if (pathname === '/appointment') return 'Call +1-519-252-2752 to book an appointment with Uptown Physiotherapy Clinic for physiotherapy, sports injury rehab, or related rehabilitation services.';
  if (pathname === '/privacy') return 'Learn how Uptown Physiotherapy Clinic approaches privacy, consent, patient rights, referrals, benefits, and Ontario physiotherapy care standards.';
  if (pathname === '/terms') return 'Review the website terms and conditions for Uptown Physiotherapy Clinic, including appointment requests, billing, and general information limits.';
  if (pathname === '/privacy-policy') return 'Review how Uptown Physiotherapy Clinic approaches privacy, confidentiality, consent, and personal health information submitted through the website.';
  if (pathname === '/legal-notice') return 'Read the legal notice for Uptown Physiotherapy Clinic, including limits on website information, emergencies, and coverage rules.';
  if (section === 'services') return services.find((item) => item.slug === slug)?.short;
  if (section === 'news') return news.find((item) => item.slug === slug)?.excerpt;
  if (section === 'team') return team.find((item) => item.slug === slug)?.bio;
  return defaultDescription;
};

const imageFor = (pathname) => {
  const [, section, slug] = pathname.split('/');
  if (section === 'services') return absolute(services.find((item) => item.slug === slug)?.hero || defaultImage);
  if (section === 'news') return absolute(news.find((item) => item.slug === slug)?.image || defaultImage);
  if (section === 'team') return absolute(team.find((item) => item.slug === slug)?.photo || defaultImage);
  return defaultImage;
};

const breadcrumb = (items) => ({
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absolute(item.path),
  })),
});

const schemaFor = (pathname) => {
  const canonical = absolute(pathname);
  const [, section, slug] = pathname.split('/');
  const graph = [
    clinic,
    {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      name: clinicName,
      url: siteUrl,
      publisher: { '@id': `${siteUrl}/#clinic` },
    },
    {
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: pageTitle(pathname),
      description: descriptionFor(pathname),
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#clinic` },
    },
  ];

  if (pathname === '/') {
    graph.push(breadcrumb([{ name: 'Home', path: '/' }]));
  } else {
    graph.push(breadcrumb([
      { name: 'Home', path: '/' },
      { name: pageTitle(pathname).replace(` | ${clinicName}`, ''), path: pathname },
    ]));
  }

  if (pathname === '/services') {
    graph.push({
      '@type': 'ItemList',
      name: 'Physiotherapy Services',
      itemListElement: services.map((service, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'MedicalTherapy',
          name: service.name,
          description: service.short,
          url: absolute(`/services/${service.slug}`),
        },
      })),
    });
  }

  if (section === 'services') {
    const service = services.find((item) => item.slug === slug);
    if (service) {
      graph.push({
        '@type': 'MedicalTherapy',
        name: service.name,
        description: service.short,
        url: canonical,
        image: absolute(service.hero),
        provider: { '@id': `${siteUrl}/#clinic` },
        therapyType: 'Physical therapy and rehabilitation service',
      });
    }
  }

  if (pathname === '/team') {
    graph.push({
      '@type': 'ItemList',
      name: 'Physiotherapy Team',
      itemListElement: team.map((member, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Person',
          name: member.name,
          jobTitle: member.role,
          image: absolute(member.photo),
          url: absolute(`/team/${member.slug}`),
        },
      })),
    });
  }

  if (section === 'team') {
    const member = team.find((item) => item.slug === slug);
    if (member) {
      graph.push({
        '@type': 'Person',
        name: member.name,
        jobTitle: member.role,
        image: absolute(member.photo),
        description: member.bio,
        worksFor: { '@id': `${siteUrl}/#clinic` },
      });
    }
  }

  if (pathname === '/news') {
    graph.push({
      '@type': 'Blog',
      name: 'Uptown Physiotherapy Clinic Blog',
      url: canonical,
      publisher: { '@id': `${siteUrl}/#clinic` },
      blogPost: news.map((article) => ({ '@id': `${absolute(`/news/${article.slug}`)}#article` })),
    });
  }

  if (section === 'news') {
    const article = news.find((item) => item.slug === slug);
    if (article) {
      graph.push({
        '@type': 'BlogPosting',
        '@id': `${canonical}#article`,
        headline: article.title,
        description: article.excerpt,
        image: absolute(article.image),
        datePublished: article.isoDate || '2026-01-01',
        dateModified: article.isoDate || '2026-01-01',
        author: { '@type': 'Organization', name: clinicName },
        publisher: { '@id': `${siteUrl}/#clinic` },
        mainEntityOfPage: { '@id': `${canonical}#webpage` },
      });
    }
  }

  if (pathname === '/contact' || pathname === '/appointment') {
    graph.push({
      '@type': 'ContactPage',
      name: pageTitle(pathname),
      url: canonical,
      mainEntity: { '@id': `${siteUrl}/#clinic` },
    });
  }

  return { '@context': 'https://schema.org', '@graph': graph };
};

const Seo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const title = pageTitle(pathname);
    const description = descriptionFor(pathname);
    const canonicalUrl = absolute(pathname);
    const previewImage = imageFor(pathname);
    const schema = schemaFor(pathname);

    document.title = title;

    const setMeta = (selector, attrs) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        Object.entries(attrs.match).forEach(([name, value]) => element.setAttribute(name, value));
        document.head.appendChild(element);
      }
      Object.entries(attrs.values).forEach(([name, value]) => element.setAttribute(name, value));
    };

    setMeta('meta[name="description"]', {
      match: { name: 'description' },
      values: { content: description || title },
    });
    setMeta('meta[name="keywords"]', {
      match: { name: 'keywords' },
      values: { content: defaultKeywords },
    });
    setMeta('meta[name="robots"]', {
      match: { name: 'robots' },
      values: { content: 'index, follow, max-image-preview:large' },
    });
    setMeta('meta[name="geo.region"]', {
      match: { name: 'geo.region' },
      values: { content: 'CA-ON' },
    });
    setMeta('meta[name="geo.placename"]', {
      match: { name: 'geo.placename' },
      values: { content: 'Windsor' },
    });
    setMeta('meta[property="og:type"]', {
      match: { property: 'og:type' },
      values: { content: pathname.startsWith('/news/') ? 'article' : 'website' },
    });
    setMeta('meta[property="og:title"]', {
      match: { property: 'og:title' },
      values: { content: title },
    });
    setMeta('meta[property="og:description"]', {
      match: { property: 'og:description' },
      values: { content: description || title },
    });
    setMeta('meta[property="og:url"]', {
      match: { property: 'og:url' },
      values: { content: canonicalUrl },
    });
    setMeta('meta[property="og:image"]', {
      match: { property: 'og:image' },
      values: { content: previewImage },
    });
    setMeta('meta[name="twitter:card"]', {
      match: { name: 'twitter:card' },
      values: { content: 'summary_large_image' },
    });
    setMeta('meta[name="twitter:title"]', {
      match: { name: 'twitter:title' },
      values: { content: title },
    });
    setMeta('meta[name="twitter:description"]', {
      match: { name: 'twitter:description' },
      values: { content: description || title },
    });
    setMeta('meta[name="twitter:image"]', {
      match: { name: 'twitter:image' },
      values: { content: previewImage },
    });

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    let jsonLd = document.getElementById('structured-data');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.id = 'structured-data';
      jsonLd.type = 'application/ld+json';
      document.head.appendChild(jsonLd);
    }
    jsonLd.textContent = JSON.stringify(schema);
  }, [pathname]);

  return null;
};

export default Seo;
